/**
 * SdmCodeGen — derives SDM and task-template JSON from the current LinkDef list.
 *
 * Handles:
 *   - Direct populate links (mode='populate', no formula branches):
 *       stamps MetaKey on source ItemDef + target task additionalData.metaKey
 *   - Remove links (mode='remove'):
 *       generates derived ItemDef + MethodDef + ItemGroupDef ItemRef (OpenEDC context)
 *       stamps targetMetaKey on target task additionalData.metaKey
 *
 * Two session storage keys are written on every link change:
 *   naomi_question_template_<tradeOid>  — SDM wire-format JSON
 *   naomi_task_template_<tradeOid>      — task array JSON
 */
import type { SDM, LinkDef, Task, Trigger, TriggerValue, ConditionClause, FormulaBranch } from '../types';
import { buildFormulaExpression } from '../expressions';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Renders a single trigger value as the text embedded into a FormalExpression
 * string comparison. A boolean source question's real stored answer is the
 * string "Yes"/"No" (never the JS literal true/false) — the Trigger picker
 * (LinkModal) stores the raw boolean for its own selected-state bookkeeping,
 * so it must be translated here, at the point it becomes comparison text,
 * or every boolean-driven trigger generates a permanently-dead
 * `== "true"`/`== "false"` comparison (see #26513-class bugs).
 */
function formatTriggerValue(v: TriggerValue | undefined): string {
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v ?? '');
}

/**
 * Generates the OpenEDC FormalExpression.text for a remove-mode MethodDef.
 * Mirrors the M.7 / M.8 / M.9 pattern from the Roof Enhancement SDM.
 */
function buildDeletionExpression(sourceItemOID: string, trigger: Trigger): string {
  const src = sourceItemOID;
  // Already a complete, self-parenthesized OR expression — embed it as `IF(${emptyCheck}, ...)`,
  // NEVER `IF((${emptyCheck}), ...)`. The extra wrap doesn't change evaluation, but it does add a
  // third leading paren that expressionParser.ts's parseDeletionTrigger regexes (which expect
  // exactly `IF((S == "") OR (S == null), ...)`, matching this string verbatim) can't match —
  // breaking round-trip editing (and, for is_empty specifically, causing it to be silently
  // mis-parsed as a different operator and corrupted on re-save).
  const emptyCheck = `(${src} == "") OR (${src} == null)`;
  const vals: TriggerValue[] = trigger.values ?? [];
  const v0 = formatTriggerValue(vals[0]);

  switch (trigger.operator) {
    case 'always':
      return 'true';

    case 'is_empty':
      return `IF(${emptyCheck}, true, null)`;

    case 'is_not_empty':
      return `IF(${emptyCheck}, null, true)`;

    case 'eq':
      return `IF(${src} == "${v0}", true, null)`;

    case 'ne':
      return `IF(${emptyCheck}, true, IF(${src} != "${v0}", true, null))`;

    case 'not_contains': {
      // "delete when multiselect does NOT include any of the given values"
      // Matches the M.7/M.8/M.9 pattern exactly.
      const checks = vals
        .map(v => `!((","+ ${src} + ",") contains ",${v},")`)
        .join(' AND ');
      return `IF(${emptyCheck}, true, IF(${checks}, true, null))`;
    }

    case 'contains': {
      // "delete when multiselect INCLUDES any of the given values"
      const checks = vals
        .map(v => `(","+ ${src} + ",") contains ",${v},"`)
        .join(' OR ');
      return `IF(${checks}, true, null)`;
    }

    case 'in': {
      const checks = vals.map(v => `${src} == "${v}"`).join(' OR ');
      return `IF((${checks}), true, null)`;
    }

    case 'not_in': {
      const checks = vals.map(v => `${src} != "${v}"`).join(' AND ');
      return `IF(${emptyCheck}, true, IF((${checks}), true, null))`;
    }

    case 'gt':
      return `IF(${emptyCheck}, null, IF(parseFloat(${src}) > ${v0}, true, null))`;

    case 'gte':
      return `IF(${emptyCheck}, null, IF(parseFloat(${src}) >= ${v0}, true, null))`;

    case 'lt':
      return `IF(${emptyCheck}, null, IF(parseFloat(${src}) < ${v0}, true, null))`;

    case 'lte':
      return `IF(${emptyCheck}, null, IF(parseFloat(${src}) <= ${v0}, true, null))`;

    default:
      return 'null';
  }
}

/**
 * Returns the next available OID for a given prefix ('I' or 'M').
 * Scans all existing OIDs of the form `prefix.N`, finds the max N, returns `prefix.(N+1)`.
 */
function nextOID(existingOIDs: string[], prefix: 'I' | 'M'): string {
  const max = existingOIDs
    .filter(id => id.startsWith(`${prefix}.`))
    .map(id => parseInt(id.slice(prefix.length + 1), 10))
    .filter(n => !isNaN(n))
    .reduce((a, b) => Math.max(a, b), 0);
  return `${prefix}.${max + 1}`;
}

// ---------------------------------------------------------------------------
// Formula decision-table builders (mode='formula')
// ---------------------------------------------------------------------------

/** OpenEDC boolean expression for one decision-table clause (evaluator-safe — see docs §9). */
function buildClauseExpression(clause: ConditionClause): string {
  const src = clause.field;
  const vals = clause.values ?? [];
  const v0 = formatTriggerValue(vals[0]);
  switch (clause.op) {
    case 'eq':
      return vals.length > 1
        ? '(' + vals.map(v => `${src} == "${v}"`).join(' OR ') + ')'
        : `${src} == "${v0}"`;
    case 'ne':
      return `${src} != "${v0}"`;
    case 'gt':  return `parseFloat(${src}) > ${v0}`;
    case 'gte': return `parseFloat(${src}) >= ${v0}`;
    case 'lt':  return `parseFloat(${src}) < ${v0}`;
    case 'lte': return `parseFloat(${src}) <= ${v0}`;
    case 'contains':
      return vals.length > 1
        ? '(' + vals.map(v => `("," + ${src} + ",") contains ",${v},"`).join(' OR ') + ')'
        : `("," + ${src} + ",") contains ",${v0},"`;
    case 'not_contains':
      return (vals.length ? vals : ['']).map(v => `!(("," + ${src} + ",") contains ",${v},")`).join(' AND ');
    case 'is_empty':     return `(${src} == "") OR (${src} == null)`;
    case 'is_not_empty': return `!((${src} == "") OR (${src} == null))`;
    default:             return 'true';
  }
}

/** Join a branch's clauses into one OpenEDC boolean expression, honoring each clause's
 *  AND/OR connector (the first clause has none). Each clause is parenthesized; the connectors
 *  apply left-to-right under OpenEDC/JS precedence (&& binds tighter than ||). */
function buildBranchCondition(conds: ConditionClause[]): string {
  const valid = (conds ?? []).filter(c => c.field);
  if (!valid.length) return 'true';
  return valid.reduce((acc, c, i) => {
    const expr = `(${buildClauseExpression(c)})`;
    if (i === 0) return expr;
    const op = c.connector === 'OR' ? 'OR' : 'AND';
    return `${acc} ${op} ${expr}`;
  }, '');
}

/**
 * Builds the deletion FormalExpression for a remove link authored via the condition builder.
 * Each branch is one rule; the task is deleted when ANY branch matches, so the branch
 * conditions are OR'd: IF((branch1) OR (branch2) ..., true, null). With no usable conditions
 * it falls back to unconditional deletion ("true"), matching the 'always' trigger.
 */
export function buildDeletionExpressionFromBranches(branches: FormulaBranch[]): string {
  const branchConds = (branches ?? [])
    .map(br => buildBranchCondition(br.conditions))
    .filter(c => c && c !== 'true');
  if (!branchConds.length) return 'true';
  const joined = branchConds.map(c => `(${c})`).join(' OR ');
  return `IF(${joined}, true, null)`;
}

/**
 * Builds the full IF / ELSE-IF / ELSE decision-table FormalExpression for a formula link.
 * Each branch becomes `IF(<condition>, <value>, <rest>)`; the final ELSE is the default value.
 * Every IF is 3-arg and every clause is evaluator-safe (docs §9).
 */
export function buildFormulaTableExpression(link: LinkDef): string {
  const p = link.populate;
  if (!p) return '0';
  const branches = p.branches ?? [];
  const def =
    p.defaultValue === undefined || p.defaultValue === '' ? '0' : String(p.defaultValue);
  let expr = def;
  for (let i = branches.length - 1; i >= 0; i--) {
    const br = branches[i];
    const cond = buildBranchCondition(br.conditions);
    const value = buildFormulaExpression(br.formulaTemplate, br.formulaVars) || '0';
    expr = `IF(${cond}, ${value}, ${expr})`;
  }
  return expr;
}

/**
 * Builds the FormalExpression text for a Switch-Plan link: an is_empty guard branch (value = the
 * fallback plan UID) plus one match-clause branch (value = the picked plan UID), default =
 * fallback. Thin wrapper over the EXISTING buildFormulaTableExpression — its "value" side is
 * content-agnostic pure text substitution (no numeric coercion, no auto-quoting), so a pre-quoted
 * JSON-string plan UID passes through verbatim and the resulting 2-branch table round-trips
 * losslessly through the UNMODIFIED parseFormulaTableExpression (verified empirically — see the
 * "POINT 3" tests in SdmCodeGen.switchPlanRoundTrip.test.ts). Neither generator needed a bespoke
 * hand-rolled string builder.
 */
export function buildSwitchPlanExpression(
  sourceItemOID: string,
  trigger: Trigger,
  pickedPlanUid: string,
  fallbackPlanUid: string,
): string {
  const q = (s: string) => JSON.stringify(s);
  const branches: FormulaBranch[] = [
    { conditions: [{ field: sourceItemOID, op: 'is_empty', values: [] }], formulaTemplate: q(fallbackPlanUid), formulaVars: {} },
    { conditions: [{ field: sourceItemOID, op: trigger.operator, values: trigger.values }], formulaTemplate: q(pickedPlanUid), formulaVars: {} },
  ];
  return buildFormulaTableExpression({ populate: { branches, defaultValue: q(fallbackPlanUid) } } as unknown as LinkDef);
}

// ---------------------------------------------------------------------------
// applyRemoveLinkToSdm
// ---------------------------------------------------------------------------

/**
 * For each remove-mode LinkDef, adds a derived ItemDef + MethodDef pair to the SDM
 * and wires them via an ItemRef in the appropriate ItemGroupDef.
 *
 * Idempotent: skips a link if a derived ItemDef with the same MetaKey already exists.
 */
function applyRemoveLinkToSdm(sdm: SDM, removeLinks: LinkDef[]): SDM {
  if (!removeLinks.length) return sdm;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let tv: any = (sdm as any).TemplateVersion?.[0];
  if (!tv) return sdm;

  // Work on mutable copies of the arrays we'll extend
  let itemDefs: any[] = [...(tv.ItemDef ?? [])];
  let methodDefs: any[] = [...(tv.MethodDef ?? [])];
  const itemGroupDefs: any[] = tv.ItemGroupDef
    ? tv.ItemGroupDef.map((g: any) => ({
        ...g,
        ItemRef: [...(g.ItemRef ?? [])],
      }))
    : [];

  for (const link of removeLinks) {
    const branches = link.remove?.branches;
    const hasBranches = !!branches?.length;
    // Anchor question for the ItemGroup lookup: the link's source, else the first condition's field.
    const anchorOID = link.sourceItemOID
      || (branches?.[0]?.conditions?.find((c: ConditionClause) => c.field)?.field ?? '');
    if (!anchorOID || !link.targetMetaKey || (!link.trigger && !hasBranches)) continue;

    const newText = hasBranches
      ? buildDeletionExpressionFromBranches(branches!)
      : buildDeletionExpression(link.sourceItemOID!, link.trigger);

    // The deletion flag's identity is its MetaKey. The _derived marker does NOT survive the wire
    // round-trip, and a trigger-based remove's source question differs from the derived flag — so
    // match the existing derived item by MetaKey, not by sourceItemOID or _derived.
    const existingDerived = itemDefs.find((item: any) => item.MetaKey === link.targetMetaKey);
    const existingRef = existingDerived
      ? itemGroupDefs.flatMap((g: any) => g.ItemRef ?? [])
          .find((r: any) => r.ItemOID === existingDerived.OID && r.MethodOID)
      : undefined;

    // Edit-in-place: an existing wired deletion flag is re-saved → regenerate its MethodDef text
    // (no duplicate ItemDef/MethodDef). This is what makes editing a reconstructed Remove link
    // update M.x in place instead of minting a second derived item.
    if (existingDerived && existingRef) {
      const mi = methodDefs.findIndex((m: any) => m.OID === existingRef.MethodOID);
      if (mi >= 0) {
        methodDefs[mi] = { ...methodDefs[mi], FormalExpression: { Context: 'OpenEDC', text: newText } };
      }
      continue;
    }

    // Generate collision-free OIDs. Reuse the existing derived item's OID when one exists for this
    // MetaKey but wasn't wired yet; only mint a new ItemDef when there is none.
    const allItemOIDs = itemDefs.map((i: any) => i.OID as string);
    const allMethodOIDs = methodDefs.map((m: any) => m.OID as string);
    const itemOID = existingDerived?.OID ?? nextOID(allItemOIDs, 'I');
    const methodOID = nextOID(allMethodOIDs, 'M');

    // Build the derived ItemDef (only when it doesn't already exist).
    if (!existingDerived) {
      const derivedItem: any = {
        Question: {
          TranslatedText: {
            lang: 'en',
            text: `(Derived) ${link.targetMetaKey}`,
          },
        },
        OID: itemOID,
        Name: link.targetMetaKey,
        DataType: 'integer',
        MetaKey: link.targetMetaKey,
        _derived: true,
      };
      itemDefs = [...itemDefs, derivedItem];
    }

    // Build the MethodDef
    const methodDef: any = {
      Description: {
        TranslatedText: {
          lang: 'en',
          text: `Auto-generated deletion rule for MetaKey: ${link.targetMetaKey}`,
        },
      },
      FormalExpression: {
        Context: 'OpenEDC',
        text: newText,
      },
      OID: methodOID,
      Name: methodOID,
    };
    methodDefs = [...methodDefs, methodDef];

    // Wire the ItemRef into the ItemGroupDef that contains sourceItemOID
    // (fall back to the first group if not found)
    const groupIdx = itemGroupDefs.findIndex((g: any) =>
      (g.ItemRef ?? []).some((ref: any) => ref.ItemOID === anchorOID),
    );
    const targetGroupIdx = groupIdx >= 0 ? groupIdx : 0;
    if (itemGroupDefs[targetGroupIdx]) {
      itemGroupDefs[targetGroupIdx] = {
        ...itemGroupDefs[targetGroupIdx],
        ItemRef: [
          ...(itemGroupDefs[targetGroupIdx].ItemRef ?? []),
          {
            ItemOID: itemOID,
            Mandatory: 'No',
            MethodOID: methodOID,
            IsVisible: 'No',
          },
        ],
      };
    }
  }

  // Rebuild TemplateVersion[0] with updated arrays
  return {
    ...sdm,
    TemplateVersion: (sdm as any).TemplateVersion.map((v: any, i: number) =>
      i === 0
        ? { ...v, ItemDef: itemDefs, MethodDef: methodDefs, ItemGroupDef: itemGroupDefs }
        : v,
    ),
  };
}

// ---------------------------------------------------------------------------
// applySwitchPlanLinkToSdm
// ---------------------------------------------------------------------------

/**
 * For each switchPlan-mode LinkDef, adds a derived ItemDef + MethodDef pair to the SDM (holding
 * the resolved plan UID as a text value) and wires them via an ItemRef in the appropriate
 * ItemGroupDef. Modeled closely on {@link applyRemoveLinkToSdm} just above: the derived item's
 * identity is its MetaKey (the `_derived` marker does not survive the wire round-trip), so an
 * edit re-save regenerates the SAME MethodDef text in place instead of minting a second derived
 * item or orphaning the first.
 */
export function applySwitchPlanLinkToSdm(sdm: SDM, switchPlanLinks: LinkDef[]): SDM {
  if (!switchPlanLinks.length) return sdm;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tv: any = (sdm as any).TemplateVersion?.[0];
  if (!tv) return sdm;

  let itemDefs: any[] = [...(tv.ItemDef ?? [])];
  let methodDefs: any[] = [...(tv.MethodDef ?? [])];
  const itemGroupDefs: any[] = tv.ItemGroupDef
    ? tv.ItemGroupDef.map((g: any) => ({
        ...g,
        ItemRef: [...(g.ItemRef ?? [])],
      }))
    : [];

  for (const link of switchPlanLinks) {
    const cfg = link.switchPlan;
    const anchorOID = link.sourceItemOID;
    if (!cfg || !anchorOID || !link.targetMetaKey || !link.trigger) continue;

    const newText = buildSwitchPlanExpression(anchorOID, link.trigger, cfg.pickedPlanUid, cfg.fallbackPlanUid);

    // The derived item's identity is its MetaKey (same reasoning as applyRemoveLinkToSdm: the
    // _derived marker does NOT survive the wire round-trip, and the trigger question differs
    // from the derived flag) — match by MetaKey, not by sourceItemOID or _derived.
    const existingDerived = itemDefs.find((item: any) => item.MetaKey === link.targetMetaKey);
    const existingRef = existingDerived
      ? itemGroupDefs.flatMap((g: any) => g.ItemRef ?? [])
          .find((r: any) => r.ItemOID === existingDerived.OID && r.MethodOID)
      : undefined;

    // Edit-in-place: an existing wired switch-plan item is re-saved → regenerate its MethodDef
    // text (no duplicate ItemDef/MethodDef).
    if (existingDerived && existingRef) {
      const mi = methodDefs.findIndex((m: any) => m.OID === existingRef.MethodOID);
      if (mi >= 0) {
        methodDefs[mi] = { ...methodDefs[mi], FormalExpression: { Context: 'OpenEDC', text: newText } };
      }
      continue;
    }

    // Generate collision-free OIDs. Reuse the existing derived item's OID when one exists for this
    // MetaKey but wasn't wired yet; only mint a new ItemDef when there is none.
    const allItemOIDs = itemDefs.map((i: any) => i.OID as string);
    const allMethodOIDs = methodDefs.map((m: any) => m.OID as string);
    const itemOID = existingDerived?.OID ?? nextOID(allItemOIDs, 'I');
    const methodOID = nextOID(allMethodOIDs, 'M');

    if (!existingDerived) {
      const derivedItem: any = {
        Question: {
          TranslatedText: {
            lang: 'en',
            text: `(Derived) ${link.targetMetaKey}`,
          },
        },
        OID: itemOID,
        Name: link.targetMetaKey,
        DataType: 'text',
        MetaKey: link.targetMetaKey,
        _derived: true,
      };
      itemDefs = [...itemDefs, derivedItem];
    }

    const methodDef: any = {
      Description: {
        TranslatedText: {
          lang: 'en',
          text: `Auto-generated plan-switch rule for MetaKey: ${link.targetMetaKey}`,
        },
      },
      FormalExpression: {
        Context: 'OpenEDC',
        text: newText,
      },
      OID: methodOID,
      Name: methodOID,
    };
    methodDefs = [...methodDefs, methodDef];

    // Wire the ItemRef into the ItemGroupDef that contains the trigger question (fall back to the
    // first group if not found; mint a fresh group when there is no ItemGroupDef at all yet — a
    // minimal/authoring-time SDM may not have one — so the wiring is never silently dropped).
    const newRef = { ItemOID: itemOID, Mandatory: 'No', MethodOID: methodOID, IsVisible: 'No' };
    const groupIdx = itemGroupDefs.findIndex((g: any) =>
      (g.ItemRef ?? []).some((ref: any) => ref.ItemOID === anchorOID),
    );
    const targetGroupIdx = groupIdx >= 0 ? groupIdx : 0;
    if (itemGroupDefs[targetGroupIdx]) {
      itemGroupDefs[targetGroupIdx] = {
        ...itemGroupDefs[targetGroupIdx],
        ItemRef: [...(itemGroupDefs[targetGroupIdx].ItemRef ?? []), newRef],
      };
    } else {
      itemGroupDefs.push({ OID: `IG.${itemGroupDefs.length + 1}`, ItemRef: [newRef] });
    }
  }

  return {
    ...sdm,
    TemplateVersion: (sdm as any).TemplateVersion.map((v: any, i: number) =>
      i === 0
        ? { ...v, ItemDef: itemDefs, MethodDef: methodDefs, ItemGroupDef: itemGroupDefs }
        : v,
    ),
  };
}

// ---------------------------------------------------------------------------
// applyFormulaLinkToSdm
// ---------------------------------------------------------------------------

/**
 * For each formula-mode LinkDef, generates the MethodDef (IF/ELSE decision table) and the
 * ItemGroupDef ItemRef that wire its derived ItemDef so MethodDefCalculator computes it.
 *
 * The derived ItemDef itself is created in MappingPane.saveLink (live SDM) and the link's
 * sourceItemOID is rebound to it; here we only add the calculation. It is regenerated fresh on
 * every sync (never accumulates) and self-cleans when the link is deleted.
 */
function applyFormulaLinkToSdm(sdm: SDM, formulaLinks: LinkDef[]): SDM {
  if (!formulaLinks.length) return sdm;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tv: any = (sdm as any).TemplateVersion?.[0];
  if (!tv) return sdm;

  let methodDefs: any[] = [...(tv.MethodDef ?? [])];
  const itemDefs: any[] = tv.ItemDef ?? [];
  const itemGroupDefs: any[] = tv.ItemGroupDef
    ? tv.ItemGroupDef.map((g: any) => ({ ...g, ItemRef: [...(g.ItemRef ?? [])] }))
    : [];

  for (const link of formulaLinks) {
    const derivedOID = link.sourceItemOID;
    if (!derivedOID) continue;

    // Wire a derived ItemDef saveLink created (_derived), OR an existing computed item the link
    // points at (a reloaded formula whose _derived flag did not survive the wire round-trip).
    const derived = itemDefs.find((it: any) => it.OID === derivedOID
      && (it._derived === true || itemGroupDefs.some((g: any) =>
        (g.ItemRef ?? []).some((r: any) => r.ItemOID === derivedOID && r.MethodOID))));
    if (!derived) continue;

    // If already wired (e.g. editing an existing formula), REGENERATE the MethodDef text in place
    // so edits to the branches take effect — do not skip.
    const wiredRef = itemGroupDefs
      .flatMap((g: any) => g.ItemRef ?? [])
      .find((r: any) => r.ItemOID === derivedOID && r.MethodOID);
    if (wiredRef) {
      const idx = methodDefs.findIndex((m: any) => m.OID === wiredRef.MethodOID);
      if (idx >= 0) {
        methodDefs[idx] = {
          ...methodDefs[idx],
          FormalExpression: { Context: 'OpenEDC', text: buildFormulaTableExpression(link) },
        };
      }
      continue;
    }

    const methodOID = nextOID(methodDefs.map((m: any) => m.OID as string), 'M');
    const cleanName = link.populate?.formulaName
      ? `M.CALC_${String(link.populate.formulaName).replace(/\s+/g, '')}`
      : methodOID;
    methodDefs = [...methodDefs, {
      OID: methodOID,
      Name: cleanName,
      Description: {
        TranslatedText: {
          lang: 'en',
          text: `Auto-generated formula for MetaKey: ${derived.MetaKey ?? derivedOID}`,
        },
      },
      FormalExpression: {
        Context: 'OpenEDC',
        text: buildFormulaTableExpression(link),
      },
    }];

    // Wire the ItemRef into the group holding the first referenced question (else the first group).
    const firstVarOID = Object.values(link.populate?.branches?.[0]?.formulaVars ?? {})
      .find(Boolean) as string | undefined;
    let groupIdx = firstVarOID
      ? itemGroupDefs.findIndex((g: any) =>
          (g.ItemRef ?? []).some((r: any) => r.ItemOID === firstVarOID))
      : -1;
    if (groupIdx < 0) groupIdx = 0;
    if (itemGroupDefs[groupIdx]) {
      itemGroupDefs[groupIdx] = {
        ...itemGroupDefs[groupIdx],
        ItemRef: [
          ...(itemGroupDefs[groupIdx].ItemRef ?? []),
          {
            ItemOID: derivedOID,
            Mandatory: 'No',
            MethodOID: methodOID,
            ...(link.populate?.formulaVisible === false ? { IsVisible: 'No' } : {}),
          },
        ],
      };
    }
  }

  return {
    ...sdm,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    TemplateVersion: (sdm as any).TemplateVersion.map((v: any, i: number) =>
      i === 0 ? { ...v, MethodDef: methodDefs, ItemGroupDef: itemGroupDefs } : v,
    ),
  };
}

// ---------------------------------------------------------------------------
// applyLinksToSdm
// ---------------------------------------------------------------------------

/** Returns a new SDM with all link-derived changes applied:
 *  - populate links: MetaKey stamped on source ItemDefs
 *  - remove links: derived ItemDef + MethodDef + ItemGroupDef ItemRef generated
 */
export function applyLinksToSdm(sdm: SDM, links: LinkDef[]): SDM {
  const tv = sdm.TemplateVersion[0];
  if (!tv) return sdm;

  // 1. Populate links — stamp MetaKey on source ItemDefs
  const populateLinks = links.filter(
    l => l.mode === 'populate' && !l.populate?.branches?.length && l.sourceItemOID && l.sourceMetaKey,
  );
  const updatedItems = tv.ItemDef.map((item: any) => {
    const link = populateLinks.find(l => l.sourceItemOID === item.OID);
    return link ? { ...item, MetaKey: link.sourceMetaKey } : item;
  });

  let result: SDM = {
    ...sdm,
    TemplateVersion: sdm.TemplateVersion.map((v, i) =>
      i === 0 ? { ...v, ItemDef: updatedItems } : v,
    ),
  };

  // 2. Remove links — generate derived ItemDef + MethodDef + ItemGroupDef wiring.
  //    A removal can be a single trigger (sourceItemOID) or condition-builder branches.
  const removeLinks = links.filter(
    l => l.mode === 'remove' && l.targetMetaKey
      && (l.sourceItemOID || l.remove?.branches?.length),
  );
  if (removeLinks.length > 0) {
    result = applyRemoveLinkToSdm(result, removeLinks);
  }

  // 3. Formula links — generate the MethodDef + ItemRef that compute each derived item
  const formulaLinks = links.filter(l => l.mode === 'formula' && l.sourceItemOID);
  if (formulaLinks.length > 0) {
    result = applyFormulaLinkToSdm(result, formulaLinks);
  }

  // 4. Switch-Plan links — generate the derived ItemDef + MethodDef + ItemGroupDef wiring that
  //    resolves to the picked (or fallback) automation-plan UID.
  const switchPlanLinks = links.filter(
    l => l.mode === 'switchPlan' && l.switchPlan && l.sourceItemOID && l.targetMetaKey,
  );
  if (switchPlanLinks.length > 0) {
    result = applySwitchPlanLinkToSdm(result, switchPlanLinks);
  }

  return result;
}

// ---------------------------------------------------------------------------
// applyLinksToTasks
// ---------------------------------------------------------------------------

/** Returns an updated task array with additionalData.metaKey set for all
 *  populate AND remove links (both need the MetaKey on the task for ASE to act). */
export function applyLinksToTasks(tasks: Task[], links: LinkDef[]): Task[] {
  const actionableLinks = links.filter(
    l => (l.mode === 'populate' || l.mode === 'remove' || l.mode === 'formula' || l.mode === 'switchPlan')
      && l.targetMetaKey,
  );

  const stamped = tasks.map(t => {
    const link = actionableLinks.find(l => l.targetTaskIds.includes(t.id));
    if (!link || !link.targetMetaKey) return t;

    const existingAd =
      typeof t.additionalData === 'object' && t.additionalData
        ? (t.additionalData as Record<string, unknown>)
        : {};
    return { ...t, additionalData: { ...existingAd, metaKey: link.targetMetaKey } };
  });

  // Switch-Plan links additionally need `_raw.actions[actionIndex].resolvePlanFromOwnMetaKey`
  // stamped so `tasksToStorage`'s verbatim `_raw` pass-through carries the flag to the wire —
  // the mechanism the already-shipped taskTemplateReconciler.ts (tenant-web) reads at runtime.
  const switchPlanLinks = links.filter(l => l.mode === 'switchPlan' && l.switchPlan && l.targetMetaKey);
  return switchPlanLinks.length ? applySwitchPlanLinksToTasks(stamped, switchPlanLinks) : stamped;
}

/**
 * Returns a NEW tasks array where each task targeted by a switchPlan link has (a)
 * `additionalData.metaKey` set to the link's derived MetaKey and (b)
 * `_raw.actions[link.switchPlan.actionIndex].resolvePlanFromOwnMetaKey` set to `true` —
 * immutably, preserving every other field on that action and every other action untouched
 * (including the fallback `automationPlanMasterUid`, which is only ever READ, never rewritten).
 * Tasks not targeted by any switchPlan link are returned by reference (`===`), unchanged.
 */
export function applySwitchPlanLinksToTasks(tasks: Task[], switchPlanLinks: LinkDef[]): Task[] {
  return tasks.map(t => {
    const link = switchPlanLinks.find(l => l.switchPlan && l.targetTaskIds.includes(t.id));
    if (!link || !link.switchPlan || !link.targetMetaKey) return t;

    const existingAd =
      typeof t.additionalData === 'object' && t.additionalData
        ? (t.additionalData as Record<string, unknown>)
        : {};
    const raw: Record<string, unknown> = (t._raw && typeof t._raw === 'object') ? t._raw : {};
    const actions: Array<Record<string, unknown>> = Array.isArray(raw.actions)
      ? raw.actions as Array<Record<string, unknown>>
      : [];
    const idx = link.switchPlan.actionIndex;
    const nextActions = actions.map((a, i) => (i === idx ? { ...a, resolvePlanFromOwnMetaKey: true } : a));

    return {
      ...t,
      additionalData: { ...existingAd, metaKey: link.targetMetaKey },
      _raw: { ...raw, actions: nextActions },
    };
  });
}

// ---------------------------------------------------------------------------
// tasksToStorage
// ---------------------------------------------------------------------------

/**
 * Convert UI {@link Task}s back to the exact shape stored in the task template, so a save
 * round-trips an evolving schema without losing fields the UI never modelled.
 *
 * Each task's original object (`_raw`, captured at load) is emitted verbatim with only its
 * `additionalData` overwritten by the current (possibly metaKey-stamped) value. UI-only
 * fields (`id`, `taskNumber`, `_raw`) are never written. Tasks with no `_raw` (the bundled
 * sample) fall back to the task minus those UI-only fields.
 */
export function tasksToStorage(tasks: Task[]): Array<Record<string, unknown>> {
  return tasks.map(t => {
    if (t._raw && typeof t._raw === 'object') {
      return { ...t._raw, additionalData: t.additionalData };
    }
    // No captured original (bundled sample): emit the task minus the purely-synthetic
    // display fields. The sample carries a real `id`/`wbs`, so those are kept.
    const out: Record<string, unknown> = { ...t };
    delete out.taskNumber;
    delete out._raw;
    return out;
  });
}

// ---------------------------------------------------------------------------
// syncToSession
// ---------------------------------------------------------------------------

/**
 * Derives the updated SDM and task template from the current links, then writes
 * both to sessionStorage. Called on every link create / edit / delete.
 *
 * @param serialize - injected from NaomiStudio to avoid a circular dep on sdm-serializer.
 *                    Receives the updated (mapping-layer) SDM as `any` and returns the
 *                    wire-format object to store.
 */
export function syncToSession(
  tradeOid: string,
  sdm: SDM,
  links: LinkDef[],
  tasks: Task[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  serialize: (sdm: any) => unknown,
): void {
  if (!tradeOid || tradeOid === 'unknown') {
    console.warn('[SdmCodeGen] Skipping sync — tradeOid not resolved');
    return;
  }
  if (tasks.length === 0) {
    console.warn('[SdmCodeGen] Skipping sync — tasks array is empty');
    return;
  }

  try {
    const updatedSdm = applyLinksToSdm(sdm, links);

    // The NaomiStudio serializer filters out TemplateVersion entries where status === 'draft'.
    // TemplateVersion[0] is always the draft (live edits), so we must clear its status before
    // serializing for session storage — otherwise only old published versions get written.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const draft = (updatedSdm as any).TemplateVersion?.[0];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sdmForSession: any = draft
      ? { ...(updatedSdm as any), TemplateVersion: [{ ...draft, status: undefined }] }
      : updatedSdm;

    const updatedTasks = applyLinksToTasks(tasks, links);

    sessionStorage.setItem(
      `naomi_question_template_${tradeOid}`,
      JSON.stringify(serialize(sdmForSession)),
    );
    sessionStorage.setItem(
      `naomi_task_template_${tradeOid}`,
      JSON.stringify(tasksToStorage(updatedTasks)),
    );
  } catch (e) {
    console.warn('[SdmCodeGen] Failed to sync to sessionStorage:', e);
  }
}
